import React from 'react';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { Box, IconButton, Rating, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../../context/SalonPublicThemeContext';
import SalonPrimaryActions from '../../../client/salonDetails/SalonPrimaryActions';
import SalonPublicButton from '../../salonPublicAppearance/SalonPublicButton';
import SalonPublicStyleScope from '../../salonPublicAppearance/SalonPublicStyleScope';


const NeoDermaIdentity = ({
  data,
  mode = 'public',
  onBack,
  onToggleFavorite,
  onOpenMobileInfo,
  onBook,
  onContact,
  clientChatEnabled,
}) => {
  const { t } = useTranslation();
  const { tokens: theme } = useSalonPublicTheme();
  const preview = mode === 'preview';
  const actionProps = {
    disabled: preview,
    'aria-disabled': String(preview),
  };
  const hasRating = Number.isFinite(Number(data?.averageRating))
    && Number(data?.reviewCount) > 0;
  const mapsHref = data?.latitude != null && data?.longitude != null
    ? `https://www.google.com/maps?q=${data.latitude},${data.longitude}`
    : null;

  if (!data?.name) return null;

  return (
    <SalonPublicStyleScope
      sectionKey="header"
      component="header"
      data-testid="salon-public-hero"
      data-salon-template-block="identity"
      data-variant="architectural"
      data-density="feature"
      sx={{
        width: '100%',
        flexShrink: 0,
        color: theme.salon.onBackground,
        backgroundColor: theme.salon.surface,
        backgroundImage: `linear-gradient(${alpha(theme.salon.secondary, 0.045)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(theme.salon.secondary, 0.045)} 1px, transparent 1px)`,
        backgroundSize: '36px 36px',
        borderBottom: `1px solid ${theme.salon.border}`,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 1380,
          mx: 'auto',
          px: { xs: 2, md: 5 },
          pt: { xs: 1.5, md: 2 },
          boxSizing: 'border-box',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 2 }}>
          <SalonPublicButton
            preserveLegacyStyle
            variant="text"
            size="small"
            startIcon={<ChevronLeftIcon />}
            onClick={onBack}
            {...actionProps}
            sx={{
              minHeight: 40,
              px: 1.5,
              color: theme.salon.secondary,
              backgroundColor: theme.salon.surface,
              border: `1px solid ${theme.salon.border}`,
              borderRadius: 0,
              '&:hover': { backgroundColor: alpha(theme.salon.primary, 0.08) },
            }}
          >
            {t('actions.back')}
          </SalonPublicButton>

          <Stack direction="row" spacing={1}>
            <IconButton
              aria-label={t('landing_salon_details.informations')}
              onClick={onOpenMobileInfo}
              {...actionProps}
              sx={{
                display: { xs: 'inline-flex', md: 'none' },
                color: theme.salon.secondary,
                backgroundColor: theme.salon.surface,
                border: `1px solid ${theme.salon.border}`,
                borderRadius: 0,
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
            <IconButton
              aria-label={t('landing_salon_details.favorite')}
              onClick={onToggleFavorite}
              {...actionProps}
              sx={{
                color: data.isFavorite ? theme.salon.primary : theme.salon.secondary,
                backgroundColor: theme.salon.surface,
                border: `1px solid ${theme.salon.border}`,
                borderRadius: 0,
                '&:hover': { backgroundColor: alpha(theme.salon.primary, 0.08) },
              }}
            >
              {data.isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </IconButton>
          </Stack>
        </Stack>

        <Box
          data-testid="neo-derma-architectural-hero"
          data-layout="architectural-panels"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '72px minmax(0, 1fr) minmax(420px, 0.92fr)' },
            minHeight: { xs: 0, md: 470 },
            overflow: 'hidden',
            backgroundColor: theme.salon.surface,
            border: `1px solid ${theme.salon.border}`,
            borderBottom: 0,
          }}
        >
          <Stack
            aria-hidden="true"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              display: { xs: 'none', md: 'flex' },
              py: 3,
              color: theme.salon.onSecondary,
              backgroundColor: theme.salon.secondary,
            }}
          >
            <AutoAwesomeOutlinedIcon sx={{ color: theme.salon.primary }} />
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 900,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
              }}
            >
              Neo Derma / Institut
            </Typography>
            <Typography sx={{ color: theme.salon.primary, fontSize: '0.75rem', fontWeight: 900 }}>
              01
            </Typography>
          </Stack>

          <Stack
            justifyContent="center"
            alignItems="flex-start"
            spacing={2}
            sx={{
              minWidth: 0,
              px: { xs: 2.5, sm: 4, md: 5.5 },
              py: { xs: 4, md: 5 },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box sx={{ width: 38, height: 1, backgroundColor: theme.salon.primary }} />
              <Typography
                variant="overline"
                sx={{ color: theme.salon.primary, fontWeight: 800, letterSpacing: '0.14em' }}
              >
                {t('salonTemplates.neoDerma.subtitle')}
              </Typography>
            </Stack>

            <Typography
              component="h1"
              data-salon-text-role="title"
              sx={{
                maxWidth: 660,
                color: theme.salon.secondary,
                fontSize: { xs: '2.45rem', sm: '3.2rem', md: '4.2rem' },
                fontWeight: 600,
                lineHeight: 0.98,
                letterSpacing: '-0.055em',
                overflowWrap: 'anywhere',
              }}
            >
              {data.name}
            </Typography>

            {data.description ? (
              <Typography
                data-salon-text-role="content"
                sx={{
                  maxWidth: 570,
                  color: theme.colors.gray[600],
                  fontSize: { xs: '0.95rem', md: '1.05rem' },
                  lineHeight: 1.75,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {data.description}
              </Typography>
            ) : null}

            {!preview && typeof onBook === 'function' ? (
              <Box sx={{ pt: 0.5, width: '100%' }}>
                <SalonPrimaryActions
                  inline
                  lightSurface
                  clientChatEnabled={clientChatEnabled}
                  onBook={onBook}
                  onContact={onContact}
                  bookLabel={t('landing_salon_details.book_appointment')}
                />
              </Box>
            ) : null}
          </Stack>

          <Box
            data-testid="neo-derma-image-panel"
            sx={{
              position: 'relative',
              minHeight: { xs: 290, sm: 360, md: 470 },
              overflow: 'hidden',
              backgroundColor: theme.salon.primary,
              clipPath: { xs: 'none', md: 'polygon(11% 0, 100% 0, 100% 100%, 0 100%)' },
            }}
          >
            {data.heroPhoto ? (
              <Box
                component="img"
                src={data.heroPhoto}
                alt={data.name}
                sx={{
                  position: 'absolute',
                  inset: { xs: 0, md: '22px 0 0 28px' },
                  width: { xs: '100%', md: 'calc(100% - 28px)' },
                  height: { xs: '100%', md: 'calc(100% - 22px)' },
                  objectFit: 'cover',
                  filter: 'saturate(0.82) contrast(1.02)',
                }}
              />
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  inset: { xs: 0, md: '22px 0 0 28px' },
                  display: 'grid',
                  placeItems: 'center',
                  color: theme.salon.onSecondary,
                  backgroundColor: theme.salon.secondary,
                }}
              >
                <Typography sx={{ fontSize: { xs: '5rem', md: '8rem' }, fontWeight: 300 }}>
                  ND
                </Typography>
              </Box>
            )}

            {hasRating ? (
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  position: 'absolute',
                  insetInlineStart: { xs: 18, md: 48 },
                  bottom: { xs: 18, md: 24 },
                  px: 2,
                  py: 1.25,
                  color: theme.salon.secondary,
                  backgroundColor: alpha(theme.salon.surface, 0.94),
                  backdropFilter: 'blur(12px)',
                  borderInlineStart: `4px solid ${theme.salon.primary}`,
                  boxShadow: `0 14px 30px ${alpha('#000000', 0.18)}`,
                }}
              >
                <Rating
                  value={Number(data.averageRating)}
                  precision={0.1}
                  readOnly
                  size="small"
                  aria-label={t('landing_salon_details.reviews_count', { count: data.reviewCount })}
                  sx={{ '& .MuiRating-iconFilled': { color: theme.salon.primary } }}
                />
                <Typography sx={{ color: 'inherit', fontWeight: 900 }}>
                  {Number(data.averageRating).toFixed(1)}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.colors.gray[500] }}>
                  {t('landing_salon_details.reviews_count_short', { count: data.reviewCount })}
                </Typography>
              </Stack>
            ) : null}
          </Box>
        </Box>

        <Box
          data-testid="neo-derma-information-rail"
          data-salon-text-role="content"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '72px minmax(0, 1fr)' },
            color: theme.salon.onSecondary,
            backgroundColor: theme.salon.secondary,
          }}
        >
          <Box sx={{ display: { xs: 'none', md: 'block' }, backgroundColor: theme.salon.primary }} />
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
            spacing={{ xs: 1.25, sm: 3 }}
            sx={{ px: { xs: 2.5, md: 4 }, py: 2 }}
          >
            {data.address ? (
              <Stack
                component={mapsHref ? 'a' : 'div'}
                href={!preview ? mapsHref : undefined}
                target={mapsHref && !preview ? '_blank' : undefined}
                rel={mapsHref && !preview ? 'noopener noreferrer' : undefined}
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{ maxWidth: 620, color: 'inherit', textDecoration: 'none' }}
              >
                <LocationOnOutlinedIcon sx={{ mt: 0.1, color: theme.salon.primary, fontSize: '1.1rem' }} />
                <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.55 }}>
                  {data.address}
                </Typography>
              </Stack>
            ) : null}
            {data.phone ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'inherit' }}>
                <PhoneOutlinedIcon sx={{ color: theme.salon.primary, fontSize: '1.05rem' }} />
                <Typography variant="body2" sx={{ color: 'inherit', whiteSpace: 'nowrap' }}>
                  {data.phone}
                </Typography>
              </Stack>
            ) : null}
          </Stack>
        </Box>
      </Box>
    </SalonPublicStyleScope>
  );
};

export default NeoDermaIdentity;
